import React, { useEffect, useRef, useState } from "react";
import { SITE } from "@/config/site";
import { LogoFull } from "./Logo";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#top", label: "Home" },
  { href: "#services", label: "Services" },
  { href: "https://instagram.com/UbertiCreative", label: "Instagram", external: true },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close on Escape + focus trap while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (e.key === "Tab" && menuRef.current) {
        const focusables = menuRef.current.querySelectorAll(
          "a[href], button:not([disabled])"
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    // Move focus into the menu when it opens
    const firstLink = menuRef.current?.querySelector("a[href]");
    firstLink?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[rgba(0,0,0,0.78)] border-b border-[rgba(198,162,99,0.14)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[80px] md:h-[88px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="group block logo-glow"
        >
          <LogoFull className="h-12 w-[220px] md:h-14 md:w-[260px] transition-transform duration-500 group-hover:scale-[1.03]" />
        </a>

        <nav
          aria-label="Primary"
          className="hidden md:flex items-center gap-9 text-sm tracking-wide text-[rgb(var(--bone-dim))]"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="nav-link"
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          data-testid="navbar-cta"
          className="hidden md:inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-medium btn-gold"
        >
          Start a Project
        </a>

        <button
          ref={toggleRef}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-[rgb(var(--bone))]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          id="mobile-menu"
          ref={menuRef}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          data-testid="mobile-menu"
          className="md:hidden border-t border-[rgba(198,162,99,0.14)] bg-[rgba(0,0,0,0.96)] backdrop-blur-md"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-base text-[rgb(var(--bone))]"
                {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full text-sm font-medium btn-gold"
            >
              Start a Project
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
